<template>
  <div class="wheel-widget">
    <h1 v-if="!hideInfo" class="basic-blue-title wheel-widget__header">
      {{ wheelDate.format('MMM YYYY') }}
    </h1>
    <div class="wheel-widget__container" :class="hideInfo ? '' : 'wheel-widget__container--info'">
      <div v-if="themeId === 0" class="f-h h-v-centerer">
        <span>{{ $t('message.WIDGET_WHEEL_NO_THEME') }}</span>
      </div>
      <Wheel
        v-else-if="themeData"
        :theme="themeData"
        :value="wheelDate"
        :hide-controls="hideControls"
        :hide-info="hideInfo"
        :is-resizing="resizing"
        @input="onWheelDateUpdate"
      />
      <div v-else class="f-h h-v-centerer">
        {{ this.$t('message.LOADING') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { FETCH_INDICATOR_THEMES } from '@/store/action-types';
import { GET_INDICATOR_THEMES } from '@/store/mutation-types';
// Import components
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import Wheel from '@/components/wheel.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { TIME_RANGE_FORMAT } from '@/utils/constants';

export default {

  name: 'WheelWidget',

  components: { Wheel },

  mixins: [mixinSocketHandler, mixinWidgets, mixinWidgetsBgColor],

  props: {
    themeId: {
      type: Number,
      default: 0,
    },
    hideControls: {
      type: Boolean,
      default: true,
    },
    hideInfo: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      themeLoading: false,
      themeData: null,
      wheelDate: this.$moment(),
    };
  },

  computed: {
    /**
     * Widget config
     */
    config() {
      return [
        {
          key: 'wheelTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'themeId',
          component: 'EditableLabelRadio',
          value: this.themeId,
          options: {
            class: 'mt10 mb5',
            legend: this.$t('message.THEME'),
            items: this.themeItems,
            labelKey: 'label',
            placeholder: this.$t('message.WIDGET_WHEEL_NO_THEME'),
            genericBorder: true,
          },
        },
        {
          key: 'hideControls',
          component: 'Checkbox',
          value: this.hideControls,
          options: {
            class: 'block generic-field-margin',
            title: this.$t('message.WIDGET_WHEEL_HIDE_CONTROLS'),
          },
        },
        {
          key: 'hideInfo',
          component: 'Checkbox',
          value: this.hideInfo,
          options: {
            class: 'block',
            title: this.$t('message.WIDGET_WHEEL_HIDE_INFO'),
          },
        },
        this.defaultBgColorConfig,
      ];
    },
    /**
     * Radio items for the theme id configuration
     */
    themeItems() {
      const themeListData = this.themeListData || [];
      return themeListData.map(theme => ({ id: Number(theme.id), label: theme.name }));
    },
    /**
     * Params used by fetchTheme fetching
     */
    fetchParams() {
      const range = this.wheelDate.clone();
      return {
        range: {
          end: range.endOf('month').format(TIME_RANGE_FORMAT),
          start: range.startOf('month').format(TIME_RANGE_FORMAT),
        },
        themeId: this.themeId,
        values: true,
      };
    },
    ...mapState({
      themeListData: state => state[GET_INDICATOR_THEMES.stateKey] || [],
      themeListLoading: state => state[GET_INDICATOR_THEMES.loadingKey],
    }),
  },

  watch: {
    themeId() {
      if (this.themeId > 0) {
        this.fetchTheme();
      }
    },
  },

  created() {
    if (this.editable && !this.themeListLoading) {
      this.$store.dispatch(FETCH_INDICATOR_THEMES);
    }
    if (this.themeId > 0) {
      this.fetchTheme();
    }
    this.registerSocketHandlers();
  },

  methods: {
    /**
     * Register all socket handlers
     */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.INDICATORS, (msg) => {
        if (
          this.themeData
          && this.themeData.indicators.some(indic => indic.id === Number(msg.id))
        ) {
          this.fetchTheme();
        }
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PUBLIC_INDICATOR, (msg) => {
        if (
          this.themeData
          && this.themeData.indicators.some(indic => indic.copyPublic === Number(msg.id))
        ) {
          this.fetchTheme();
        }
      });
    },
    /**
     * Fetch and store theme data, according to current themeId
     */
    fetchTheme() {
      if (this.themeLoading || !(this.themeId > 0)) return;
      this.themeLoading = true;
      this.$store.dispatch(FETCH_INDICATOR_THEMES, this.fetchParams).then((data) => {
        if (data instanceof Array) {
          ([this.themeData] = data);
        }
        this.themeLoading = false;
      });
    },
    /**
     * Event handler - Update wheel data
     * @param {moment} newDate - New date given by Wheel component
     */
    onWheelDateUpdate(newDate) {
      this.wheelDate = newDate;
      this.fetchTheme();
    },
  },
};
</script>

<style lang="scss">
.wheel-widget {
  $header-line-height: 28px;
  $header-v-padding: 8px;

  .wheel-widget__header {
    background-color: transparent;
    line-height: $header-line-height;
    padding: $header-v-padding 0;
    margin: 0;
  }

  .wheel-widget__container {
    height: 100%;
    overflow: auto;

    &--info {
      height: calc(100% - #{$header-v-padding * 2 + $header-line-height});
    }
  }
}
</style>
